/** Minimal hash router: "#/sets", "#/learn/<setId>", ... */

export type RouteContext = {
  path: string;
  params: string[];
  query: URLSearchParams;
};

export type RouteHandler = (context: RouteContext) => HTMLElement;

type Route = {
  pattern: RegExp;
  handler: RouteHandler;
};

const routes: Route[] = [];
const listeners: Array<(path: string) => void> = [];
let fallback: RouteHandler | null = null;
let outlet: HTMLElement | null = null;

/** `pattern` uses ":id" placeholders, e.g. "/learn/:id". */
export function route(pattern: string, handler: RouteHandler): void {
  const source = `^${pattern.replace(/:[^/]+/g, '([^/]+)')}$`;
  routes.push({ pattern: new RegExp(source), handler });
}

/** Called before every render with the path about to be shown. */
export function addRouteListener(listener: (path: string) => void): void {
  listeners.push(listener);
}

export function setFallback(handler: RouteHandler): void {
  fallback = handler;
}

export function navigate(path: string, replace = false): void {
  const target = `#${path}`;
  if (window.location.hash === target) {
    render();
    return;
  }
  if (replace) {
    window.location.replace(target);
  } else {
    window.location.hash = target;
  }
}

function currentPath(): string {
  const raw = window.location.hash.slice(1);
  return raw === '' ? '/sets' : raw;
}

export function render(): void {
  if (!outlet) return;

  const raw = currentPath();
  const [path, queryString = ''] = raw.split('?');
  const query = new URLSearchParams(queryString);

  for (const listener of listeners) listener(path);

  for (const entry of routes) {
    const match = entry.pattern.exec(path);
    if (match) {
      const view = entry.handler({ path, params: match.slice(1).map(decodeURIComponent), query });
      outlet.replaceChildren(view);
      window.scrollTo(0, 0);
      return;
    }
  }

  if (fallback) {
    outlet.replaceChildren(fallback({ path, params: [], query }));
  }
}

export function startRouter(mount: HTMLElement): void {
  outlet = mount;
  window.addEventListener('hashchange', render);
  if (window.location.hash === '') {
    window.location.replace('#/sets');
  }
  render();
}
